import axios from 'axios';

const BASE_URL = 'https://open.er-api.com/v6/latest/CAD';
const CACHE_KEY = 'currency_rates_cad';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Fetches latest exchange rates with CAD as base.
 * Uses localStorage for caching.
 */
export const getExchangeRates = async () => {
  try {
    const cachedData = localStorage.getItem(CACHE_KEY);
    if (cachedData) {
      const { rates, timestamp } = JSON.parse(cachedData);
      if (Date.now() - timestamp < CACHE_EXPIRY) {
        return rates;
      }
    }

    const response = await axios.get(BASE_URL);
    if (response.data && response.data.result === 'success') {
      const rates = response.data.rates;
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ rates, timestamp: Date.now() })
      );
      return rates;
    }
    throw new Error('Failed to fetch rates');
  } catch (error) {
    console.error('Currency conversion error:', error);
    // Fallback/Draft rates if API fails (approximate)
    return { CAD: 1, USD: 0.74, EUR: 0.68, GBP: 0.58, INR: 61.2 };
  }
};

/**
 * Formats a number as currency using Intl.NumberFormat.
 * @param {number} amount - The amount to format.
 * @param {string} currency - The currency code (e.g., 'USD').
 * @returns {string} - Formatted currency string.
 */
export const formatCurrency = (amount, currency = 'CAD') => {
  try {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (e) {
    return `${currency} ${amount}`;
  }
};

/**
 * Converts an amount from CAD to the target currency.
 * @param {number} amount - Amount in CAD.
 * @param {string} targetCurrency - Target currency code.
 * @param {Object} rates - Exchange rates object.
 * @returns {number} - Converted amount.
 */
export const convertFromCAD = (amount, targetCurrency, rates) => {
  const numericAmount = Number(amount);
  if (!rates || !targetCurrency || targetCurrency === 'CAD') return numericAmount;
  const rate = rates[targetCurrency];
  if (!rate) return numericAmount;
  return numericAmount * rate;
};
/**
 * Converts an amount from target currency back to CAD.
 * @param {number} amount - Amount in target currency.
 * @param {string} sourceCurrency - Source currency code.
 * @param {Object} rates - Exchange rates object.
 * @returns {number} - Amount converted to CAD.
 */
export const convertToCAD = (amount, sourceCurrency, rates) => {
  const numericAmount = Number(amount);
  if (!rates || !sourceCurrency || sourceCurrency === 'CAD') return numericAmount;
  const rate = rates[sourceCurrency];
  if (!rate || rate === 0) return numericAmount;
  return numericAmount / rate;
};
